import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';

// RUTAS RELATIVAS
import { useSegurosData } from '../../../../context/SegurosDataContext';
import { saveToFirestore } from '../../../../firebase/cleanPayload';
import BuscadorInteligente from '../../../ui/BuscadorInteligente';
import Icons from '../common/Icons';

/**
 * ModalCalificarClienteCroselling
 * Carga/Edición rápida de oportunidad con Riesgo y Subriesgo encadenados.
 */
const ModalCalificarClienteCroselling = ({ show, onClose, onSuccess, initialData }) => {
    const { dbMasters } = useSegurosData();

    // ESTADOS LOCALES DE CLIENTE Y BÚSQUEDA
    const [selectedClient, setSelectedClient] = useState(null);
    const [isProspect, setIsProspect] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);

    const [formData, setFormData] = useState({
        riesgo: '',
        subriesgo: '',
        estadoGestion: 'Pendiente',
        fechaProximoContacto: '',
        responsableAsignado: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [localError, setLocalError] = useState(null);

    // OBTENER Y ORDENAR RIESGOS DESDE dbMasters
    const sortedRiesgos = useMemo(() => {
        const riesgos = dbMasters?.riesgos || [];
        return [...riesgos].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    }, [dbMasters?.riesgos]);

    // OBTENER SUBRIESGOS SEGÚN EL RIESGO SELECCIONADO POR NOMBRE
    const sortedSubriesgos = useMemo(() => {
        if (!formData.riesgo) return [];
        const riesgoEncontrado = (dbMasters?.riesgos || []).find(r => r.nombre === formData.riesgo);
        if (!riesgoEncontrado) return [];

        const subList = riesgoEncontrado.subriesgos || riesgoEncontrado.subRiesgos || [];
        return subList.map(s => (typeof s === 'string' ? s : s.nombre || s.subriesgo)).filter(Boolean).sort();
    }, [dbMasters?.riesgos, formData.riesgo]);

    const sortedColaboradores = useMemo(() => {
        return [...(dbMasters?.colaboradores || [])].sort((a, b) => 
            (a.nombre || '').localeCompare(b.nombre || '')
        );
    }, [dbMasters?.colaboradores]);

    // HIDRATACIÓN Y RESETEO ESTRICTO DE ESTADO
    useEffect(() => {
        if (show) {
            if (initialData && initialData.id) {
                setSelectedClient({
                    id: initialData.clienteId,
                    nombre: initialData.clienteNombre,
                    apellido: initialData.clienteApellido,
                    cuit: initialData.clienteCuit,
                    dni: initialData.clienteDni,
                    telefonos: initialData.telefonos || [],
                    emails: initialData.emails || []
                });
                setIsProspect(initialData.tipoEntidad === 'PROSPECTO');
                setFormData({
                    riesgo: initialData.riesgo || (Array.isArray(initialData.productoAprobar) ? initialData.productoAprobar[0] : '') || '',
                    subriesgo: initialData.subriesgo || '',
                    estadoGestion: initialData.estadoGestion || 'Pendiente',
                    fechaProximoContacto: initialData.fechaProximoContacto || '',
                    responsableAsignado: initialData.responsableAsignado || ''
                });
            } else {
                setSelectedClient(null);
                setIsProspect(false);
                setSearchPerformed(false);
                setSearchResults([]);
                setFormData({
                    riesgo: '',
                    subriesgo: '',
                    estadoGestion: 'Pendiente',
                    fechaProximoContacto: '',
                    responsableAsignado: ''
                });
            }

            setLocalError(null);
        }
    }, [show, initialData]);

    const handleRiesgoChange = (nombreRiesgo) => {
        setFormData(prev => ({
            ...prev,
            riesgo: nombreRiesgo,
            subriesgo: ''
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // GUARDADO TRANSACCIONAL DE LA CALIFICACIÓN
    const handleSaveCalificacion = async () => {
        if (isSubmitting) return;

        if (!selectedClient) {
            setLocalError("Debes seleccionar un cliente o prospecto de la búsqueda.");
            return;
        }

        if (!formData.riesgo) {
            setLocalError("Debes seleccionar un Riesgo principal.");
            return;
        }

        setIsSubmitting(true);
        setLocalError(null);

        try {
            const extractArray = (primaryArr, fallbackArr, primarySingle, fallbackSingle) => {
                const combined = [
                    ...(Array.isArray(primaryArr) ? primaryArr : []),
                    ...(Array.isArray(fallbackArr) ? fallbackArr : []),
                    ...(typeof primarySingle === 'string' ? [primarySingle] : []),
                    ...(typeof fallbackSingle === 'string' ? [fallbackSingle] : [])
                ].filter(Boolean);
                
                return [...new Set(combined)];
            };

            const finalTelefonos = extractArray(
                selectedClient.telefonos, 
                initialData?.telefonos, 
                selectedClient.telefono, 
                initialData?.telefono
            );

            const finalEmails = extractArray(
                selectedClient.emails, 
                initialData?.emails, 
                selectedClient.email, 
                initialData?.email
            );

            const payload = {
                clienteId: selectedClient.id,
                clienteNombre: selectedClient.nombre || '',
                clienteApellido: selectedClient.apellido || '',
                clienteCuit: selectedClient.cuit || '',
                clienteDni: selectedClient.dni || '',
                telefonos: finalTelefonos,
                emails: finalEmails,

                canal: selectedClient.canal || initialData?.canal || '-',
                canalNombre: selectedClient.canalNombre || initialData?.canalNombre || '-',
                canalNombreId: selectedClient.canalNombreId || initialData?.canalNombreId || '',
                subcanalNombre: selectedClient.subcanalNombre || initialData?.subcanalNombre || '-',
                subcanalNombreId: selectedClient.subcanalNombreId || initialData?.subcanalNombreId || '',

                riesgo: formData.riesgo,
                subriesgo: formData.subriesgo || '',
                productoAprobar: formData.subriesgo 
                    ? [`${formData.riesgo} (${formData.subriesgo})`] 
                    : [formData.riesgo],

                estadoGestion: formData.estadoGestion,
                fechaCarga: initialData?.fechaCarga || new Date().toISOString().split('T')[0],
                fechaProximoContacto: formData.fechaProximoContacto || null,
                responsableAsignado: formData.responsableAsignado || '',
                tipoEntidad: isProspect ? 'PROSPECTO' : 'CLIENTE'
            };

            const collectionPath = 'public/data/clientes_croselling';
            const savedDoc = await saveToFirestore(collectionPath, payload, initialData?.id);

            // Preparamos el registro formateado completo para pasárselo al Detalle inmediatamente
            const nombreComp = [payload.clienteNombre, payload.clienteApellido].filter(Boolean).join(' ').trim();
            const recordForDetail = {
                id: savedDoc.id,
                ...payload,
                displayNombre: nombreComp || 'SIN NOMBRE',
                displayDoc: payload.clienteCuit || payload.clienteDni || '-',
                productosDisplay: payload.subriesgo ? `${payload.riesgo} (${payload.subriesgo})` : payload.riesgo
            };

            onSuccess?.(recordForDetail);
            onClose();
        } catch (error) {
            console.error("Error al guardar calificación de cliente:", error);
            setLocalError(error.message || "Error al procesar el guardado.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!show) return null;

    return createPortal(
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity text-left font-sans">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-auto max-h-[95vh] flex flex-col overflow-hidden border border-gray-100 animate-fade-in transition-all duration-300">
                
                {/* HEADER DEL MODAL */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-blue-50/50 shrink-0">
                    <div className="flex items-center gap-3">
                        <span className="bg-blue-100 text-blue-600 p-2.5 rounded-xl shrink-0">
                            <Icons.User className="w-5 h-5" />
                        </span>
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 leading-none">
                                {initialData ? 'Gestionar Calificación' : 'Calificar Cliente (Cross-Selling)'}
                            </h3>
                            <p className="text-[10px] text-blue-800/80 font-black mt-1.5 tracking-wider uppercase select-none">
                                {selectedClient ? `Asegurado: ${selectedClient.nombre} ${selectedClient.apellido || ''} • CUIT/DNI: ${selectedClient.cuit || selectedClient.dni || '-'}` : 'Búsqueda y asignación comercial'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} type="button" className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-slate-50 rounded-full cursor-pointer">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* CUERPO DEL MODAL */}
                <div className="overflow-y-auto p-8 flex-grow bg-white scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                    <div className="space-y-8">
                        
                        {localError && (
                            <div className="p-4 bg-red-50 text-red-700 rounded-xl font-bold text-xs border border-red-200 shadow-sm flex items-center gap-2">
                                <span>⚠️ {localError}</span>
                            </div>
                        )}

                        {/* SECCIÓN I: CLIENTE / PROSPECTO */}
                        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm transition-all duration-300">
                            <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">
                                Datos del Cliente / Prospecto
                            </legend>

                            {!selectedClient ? (
                                <div className="pt-2 space-y-4">
                                    <BuscadorInteligente 
                                        scope="global_entidades" 
                                        onResult={(r) => { 
                                            setSearchResults(r); 
                                            setSearchPerformed(true); 
                                        }} 
                                        onLoading={setLoadingSearch} 
                                        placeholder="Buscar por DNI, CUIT o Nombre..." 
                                        autoFocus={true} 
                                    />

                                    {!loadingSearch && searchPerformed && (
                                        <div className="space-y-2 pt-2">
                                            {searchResults.length > 0 ? (
                                                <div className="max-h-48 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                                                    {searchResults.map(client => (
                                                        <div 
                                                            key={client.id} 
                                                            onClick={() => {
                                                                setSelectedClient(client);
                                                                setIsProspect(client.type === 'prospecto');
                                                            }}
                                                            className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex justify-between items-center hover:border-blue-400 hover:bg-blue-50/50 transition-all cursor-pointer group"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-xs">
                                                                    {client.nombre?.charAt(0)}{client.apellido?.charAt(0)}
                                                                </div>
                                                                <div>
                                                                    <h4 className="font-bold text-slate-800 text-xs uppercase">{client.nombre} {client.apellido}</h4>
                                                                    <p className="text-[10px] text-slate-500 font-mono">{client.dni || client.cuit}</p>
                                                                </div>
                                                            </div>
                                                            <span className="text-xs font-bold text-blue-600 uppercase opacity-0 group-hover:opacity-100 transition-opacity">Seleccionar →</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-center text-slate-400 py-4 text-xs italic">
                                                    No se encontraron clientes o prospectos con ese criterio.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="pt-2 flex items-center justify-between bg-blue-50/60 border border-blue-200 p-4 rounded-xl">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                                            {selectedClient.nombre?.charAt(0)}{selectedClient.apellido?.charAt(0)}
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-bold text-slate-800 uppercase">{selectedClient.nombre} {selectedClient.apellido}</h4>
                                            <p className="text-xs text-slate-500 font-mono">CUIT/DNI: {selectedClient.cuit || selectedClient.dni || '-'}</p>
                                        </div>
                                    </div>
                                    {!initialData && (
                                        <button 
                                            type="button" 
                                            onClick={() => setSelectedClient(null)} 
                                            className="text-xs font-bold text-blue-600 hover:text-blue-800 uppercase underline cursor-pointer"
                                        >
                                            Cambiar Cliente
                                        </button>
                                    )}
                                </div>
                            )}
                        </fieldset>

                        {/* SECCIÓN II: RIESGO Y SUBRIESGO ENCADENADOS */}
                        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm transition-all duration-300">
                            <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">
                                Productos y Ramos
                            </legend>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1.5 pl-1 uppercase">Riesgo *</label>
                                    <select 
                                        value={formData.riesgo} 
                                        onChange={(e) => handleRiesgoChange(e.target.value)} 
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" 
                                        required
                                    >
                                        <option value="">Seleccione Riesgo...</option>
                                        {sortedRiesgos.map(r => (
                                            <option key={r.id} value={r.nombre}>
                                                {r.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1.5 pl-1 uppercase">Subriesgo / Cobertura</label>
                                    <select 
                                        name="subriesgo" 
                                        value={formData.subriesgo} 
                                        onChange={handleChange} 
                                        disabled={sortedSubriesgos.length === 0}
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all disabled:bg-slate-50 disabled:text-slate-300" 
                                    >
                                        <option value="">-- Seleccione Subriesgo --</option>
                                        {sortedSubriesgos.map((s, i) => (
                                            <option key={i} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </fieldset>

                        {/* SECCIÓN III: GESTIÓN DE AGENDA */}
                        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm transition-all duration-300">
                            <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">
                                Estado y Próximo Contacto
                            </legend>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Estado de Gestión *</label>
                                    <select 
                                        name="estadoGestion" 
                                        value={formData.estadoGestion} 
                                        onChange={handleChange} 
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-blue-700 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" 
                                        required
                                    >
                                        <option value="Pendiente">Pendiente</option>
                                        <option value="Contactado">Contactado</option>
                                        <option value="Cotizado">Cotizado</option>
                                        <option value="En Negociación">En Negociación</option>
                                        <option value="Cerrado Ganado">Cerrado Ganado</option>
                                        <option value="Cerrado Perdido">Cerrado Perdido</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Responsable Asignado</label>
                                    <select 
                                        name="responsableAsignado" 
                                        value={formData.responsableAsignado} 
                                        onChange={handleChange} 
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                    >
                                        <option value="">Seleccione...</option>
                                        {sortedColaboradores.map(v => (
                                            <option key={v.id} value={v.nombre}>{v.nombre}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Próximo Contacto (Agenda)</label>
                                    <input 
                                        type="date" 
                                        name="fechaProximoContacto" 
                                        value={formData.fechaProximoContacto} 
                                        onChange={handleChange} 
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" 
                                    />
                                </div>
                            </div>
                        </fieldset>

                    </div>
                </div>

                {/* BOTONERA INFERIOR */}
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 shrink-0">
                    <button 
                        onClick={onClose} 
                        type="button"
                        className="px-6 py-2.5 rounded-full border border-slate-300 bg-white text-slate-700 font-bold uppercase text-xs hover:bg-slate-50 shadow-sm transition-all transform active:scale-95 select-none cursor-pointer"
                    >
                        Cancelar
                    </button>
                    <button 
                        type="button" 
                        onClick={handleSaveCalificacion} 
                        disabled={isSubmitting} 
                        className="px-8 py-2.5 rounded-full text-white font-bold transition-all uppercase text-xs transform active:scale-95 min-w-[180px] shadow-lg select-none cursor-pointer bg-blue-600 hover:bg-blue-700 shadow-blue-100 disabled:opacity-50"
                    >
                        {isSubmitting ? 'Guardando...' : 'Confirmar cambios'}
                    </button>
                </div>

            </div>
        </div>,
        document.body
    );
};

export default ModalCalificarClienteCroselling;